import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoney, formatQuantity } from '@/format/money'
import {
  addDealLine,
  fetchDealCard,
  fetchStageHistory,
  removeDealLine,
  updateDealLine,
  type DealCard,
  type DealLine,
  type DealLineInput,
  type StageHistoryEntry,
} from './dealApi'
import { RecordPicker } from './RecordPicker'
import type { CardTab, CardTabContext } from './types'

/**
 * Вкладки карточки сделки (тикет #71, ТЗ §4.2).
 *
 * ## Почему это не общие вкладки каркаса
 *
 * Каркас #66 даёт четыре стандартные вкладки и точку расширения `extraTabs`. Строки со
 * скидками и история стадий — содержимое ровно этой сущности; объявлять их в каркасе
 * значило бы, что карточка товара начинает знать про воронки.
 *
 * ## Почему история стадий — своя вкладка, а не общая «История»
 *
 * Общая вкладка каркаса — это `crm.audit_log`: «кто и когда поменял поле». Она до сих пор
 * заглушка, потому что журнал наружу не выведен. История **стадий** — другой журнал
 * (`deal_stage_history`) и другой вопрос: не «кто правил карточку», а «как сделка шла по
 * воронке». Свести их в одну вкладку значило бы, что смена телефона контакта встанет в
 * один список с возвратом сделки на переговоры.
 */

/** Три состояния запроса одним компонентом: иначе их путают в каждой вкладке заново. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (!query.data) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

// ------------------------------------------------------------------ строки сделки

function LinesTable({
  lines,
  currency,
  onEdit,
}: {
  lines: DealLine[]
  currency: string
  /** Правка строки (#100); без неё таблица только читается — у закрытой сделки и без права. */
  onEdit?: (line: DealLine) => React.ReactNode
}) {
  const { t, locale } = useApp()

  if (lines.length === 0) {
    return <p className="muted">{t('card.deal.lines.empty')}</p>
  }

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('card.deal.line.product')}</th>
          <th>{t('card.deal.line.quantity')}</th>
          <th>{t('card.deal.line.price')}</th>
          <th>{t('card.deal.line.discount')}</th>
          <th>{t('card.deal.line.vat')}</th>
          <th>{t('card.deal.line.total')}</th>
          <th>{t('card.deal.line.cost')}</th>
          {onEdit && <th />}
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id}>
            <td>
              {line.productName}
              <span className="muted"> {line.productSku}</span>
            </td>
            <td>{formatQuantity(line.quantity, locale)}</td>
            <td>{formatMoney(line.unitPrice, currency, locale)}</td>
            {/* Скидка показывается и когда её нет: пустая ячейка читается как «скидка
                неизвестна», а ноль — как «продали по прайсу», и это разные утверждения
                для того, кто разбирается, почему маржа ниже плана. */}
            <td>{line.discountPercent}%</td>
            <td>
              {formatMoney(line.vatAmount, currency, locale)} <span className="muted">({line.vatRate}%)</span>
            </td>
            <td>{formatMoney(line.lineTotal, currency, locale)}</td>
            <td>
              {/* NULL значим и не равен нулю: у услуги себестоимости может не быть, у
                  товара она может быть не заполнена. Прочерк говорит «неизвестна», ноль
                  соврал бы про стопроцентную маржу. */}
              {line.lineCost === null ? t('card.deal.cost.unknown') : formatMoney(line.lineCost, currency, locale)}
            </td>
            {onEdit && <td>{onEdit(line)}</td>}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={5}>{t('card.deal.lines.total')}</td>
          <td colSpan={2}>
            {formatMoney(lines.reduce((sum, line) => sum + line.lineTotal, 0), currency, locale)}
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

/**
 * Правка позиций (#100): сумму сделки пересчитывает сервер из строк, а не клиент.
 *
 * Ответ на каждую правку — карточка целиком, и она же кладётся в кеш: свой пересчёт на
 * клиенте разошёлся бы с серверным построчным округлением ровно на копейки, которые
 * сверяет бухгалтер.
 */
function useLineMutations(recordId: string, onError: (message: string) => void) {
  const queryClient = useQueryClient()
  const { t } = useApp()
  const settle = (deal: DealCard) => {
    queryClient.setQueryData(['deal-card', recordId], deal)
    // The list shows the amount too, and the card's main tab reads it from the list row.
    void queryClient.invalidateQueries({ queryKey: ['list', 'sales.deal'] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'sales.deal', recordId] })
  }
  const fail = (error: unknown) => onError(error instanceof ApiError ? error.message : t('app.error'))
  return {
    add: useMutation({ mutationFn: (body: DealLineInput) => addDealLine(recordId, body), onSuccess: settle, onError: fail }),
    update: useMutation({
      mutationFn: ({ lineId, body }: { lineId: string; body: DealLineInput }) => updateDealLine(recordId, lineId, body),
      onSuccess: settle,
      onError: fail,
    }),
    remove: useMutation({ mutationFn: (lineId: string) => removeDealLine(recordId, lineId), onSuccess: settle, onError: fail }),
  }
}

function LineEditor({
  line,
  onSave,
  onRemove,
  busy,
}: {
  line: DealLine
  onSave: (body: DealLineInput) => void
  onRemove: () => void
  busy: boolean
}) {
  const { t } = useApp()
  const [editing, setEditing] = useState(false)
  const [quantity, setQuantity] = useState(String(line.quantity))
  const [price, setPrice] = useState(String(line.unitPrice))
  const [discount, setDiscount] = useState(String(line.discountPercent))

  if (!editing) {
    return (
      <span className="report-actions">
        <button type="button" onClick={() => setEditing(true)} aria-label={`${t('card.edit')} ${line.productName}`}>
          {t('card.edit')}
        </button>
        <button type="button" disabled={busy} onClick={onRemove} aria-label={`${t('deal.line.remove')} ${line.productName}`}>
          {t('deal.line.remove')}
        </button>
      </span>
    )
  }
  return (
    <span className="report-actions" role="group" aria-label={`${t('card.edit')} ${line.productName}`}>
      <input aria-label={t('card.deal.line.quantity')} type="number" min="0" step="any" value={quantity}
             onChange={(event) => setQuantity(event.target.value)} />
      <input aria-label={t('card.deal.line.price')} type="number" min="0" step="any" value={price}
             onChange={(event) => setPrice(event.target.value)} />
      <input aria-label={t('card.deal.line.discount')} type="number" min="0" max="100" step="any" value={discount}
             onChange={(event) => setDiscount(event.target.value)} />
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          onSave({ quantity: Number(quantity), unitPrice: Number(price), discountPercent: Number(discount) })
          setEditing(false)
        }}
      >
        {t('card.save')}
      </button>
      <button type="button" onClick={() => setEditing(false)}>
        {t('card.cancel')}
      </button>
    </span>
  )
}

function AddLineForm({ onAdd, busy }: { onAdd: (body: DealLineInput) => void; busy: boolean }) {
  const { t } = useApp()
  const [product, setProduct] = useState<{ id: string; label: string } | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [price, setPrice] = useState('')
  const [discount, setDiscount] = useState('0')
  return (
    <div className="card-gap" role="group" aria-label={t('deal.line.add')}>
      <label htmlFor="line-product">{t('card.deal.line.product')}</label>
      <RecordPicker id="line-product" entity="sales.product" searchColumn="name" extraColumn="sku"
                    value={product} onChange={setProduct} />
      <label>
        {t('card.deal.line.quantity')}{' '}
        <input type="number" min="0" step="any" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
      </label>
      {/* Пустая цена — «по прайс-листу клиента», а не ноль: бесплатная строка выглядела бы
          подарком, и согласование скидки на ней не сработало бы никогда. */}
      <label>
        {t('card.deal.line.price')}{' '}
        <input type="number" min="0" step="any" value={price} placeholder={t('deal.line.priceFromList')}
               onChange={(event) => setPrice(event.target.value)} />
      </label>
      <label>
        {t('card.deal.line.discount')}{' '}
        <input type="number" min="0" max="100" step="any" value={discount}
               onChange={(event) => setDiscount(event.target.value)} />
      </label>
      <button
        type="button"
        disabled={busy || product === null}
        onClick={() => {
          if (!product) return
          onAdd({
            productId: product.id,
            quantity: Number(quantity),
            unitPrice: price.trim() === '' ? null : Number(price),
            discountPercent: Number(discount),
          })
          setProduct(null)
          setPrice('')
        }}
      >
        {t('deal.line.add')}
      </button>
    </div>
  )
}

export function LinesPanel({ recordId }: { recordId: string }) {
  const { t, user, locale } = useApp()
  const [notice, setNotice] = useState<string | null>(null)
  const query = useQuery({
    queryKey: ['deal-card', recordId],
    queryFn: ({ signal }) => fetchDealCard(recordId, signal),
    retry: false,
  })
  const lines = useLineMutations(recordId, setNotice)
  const busy = lines.add.isPending || lines.update.isPending || lines.remove.isPending

  return (
    <AsyncSection query={query} empty={t('card.deal.lines.empty')}>
      {(deal: DealCard) => {
        // A closed deal's lines are what was invoiced or what the loss report counts.
        const editable = deal.status === 'open' && can(user, 'sales.deal', 'update')
        return (
        <div className="card-gap">
          {notice && <p className="notice notice-error" role="alert">{notice}</p>}
          <LinesTable
            lines={deal.lines}
            currency={deal.currency}
            onEdit={
              editable
                ? (line) => (
                    <LineEditor
                      line={line}
                      busy={busy}
                      onSave={(body) => {
                        setNotice(null)
                        lines.update.mutate({ lineId: line.id, body })
                      }}
                      onRemove={() => {
                        setNotice(null)
                        lines.remove.mutate(line.id)
                      }}
                    />
                  )
                : undefined
            }
          />
          {editable && (
            <AddLineForm
              busy={busy}
              onAdd={(body) => {
                setNotice(null)
                lines.add.mutate(body)
              }}
            />
          )}

          <h3>{t('card.deal.margin.title')}</h3>
          <dl className="card-section-body">
            <div className="card-field">
              <dt>{t('card.deal.margin.cost')}</dt>
              <dd>{deal.costAmount === null ? t('card.deal.cost.unknown') : formatMoney(deal.costAmount, deal.currency, locale)}</dd>
            </div>
            <div className="card-field">
              <dt>{t('card.deal.margin.amount')}</dt>
              {/* «Неизвестна», а не «ноль»: частичная себестоимость завышает маржу, и
                  это направление, которое льстит и потому не проверяется. */}
              <dd>
                {deal.marginAmount === null
                  ? t('card.deal.margin.unknown')
                  : formatMoney(deal.marginAmount, deal.currency, locale)}
              </dd>
            </div>
            <div className="card-field">
              <dt>{t('card.deal.weighted')}</dt>
              <dd>
                {formatMoney(deal.weightedAmount, deal.currency, locale)} <span className="muted">({deal.probability}%)</span>
              </dd>
            </div>
          </dl>
        </div>
        )
      }}
    </AsyncSection>
  )
}

/**
 * Вкладка «Строки»: просмотр и, у открытой сделки, правка позиций (#100). Пересчёт суммы,
 * цена из прайс-листа и построчный НДС — на сервере (`DealService.addLine`).
 */
export function dealLinesTab(): CardTab {
  return {
    id: 'lines',
    labelKey: 'card.tab.dealLines',
    render: (context: CardTabContext) => <LinesPanel recordId={context.recordId} />,
  }
}

// ------------------------------------------------------------------ история стадий

/**
 * Длительность в стадии человеческим языком.
 *
 * Секунды на экране нечитаемы: «487 231 с» не отвечает на вопрос «долго ли висела».
 * Округление до дня — сознательная потеря: разница между 5 и 5.4 днями в стадии не
 * меняет ни одного решения, а лишние знаки прячут то, что меняет.
 */
export function formatDuration(
  seconds: number | null,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (seconds === null) return '—'
  const days = Math.floor(seconds / 86400)
  if (days > 0) return t('card.deal.history.days', { days })
  const hours = Math.floor(seconds / 3600)
  if (hours > 0) return t('card.deal.history.hours', { hours })
  return t('card.deal.history.minutes', { minutes: Math.max(1, Math.floor(seconds / 60)) })
}

function HistoryTable({ entries }: { entries: StageHistoryEntry[] }) {
  const { t, locale } = useApp()

  if (entries.length === 0) {
    return <p className="muted">{t('card.deal.history.empty')}</p>
  }

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('card.deal.history.when')}</th>
          <th>{t('card.deal.history.from')}</th>
          <th>{t('card.deal.history.to')}</th>
          <th>{t('card.deal.history.spent')}</th>
          <th>{t('card.deal.history.who')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.id} className={entry.backwards ? 'stage-backwards' : undefined}>
            <td>{formatMoment(entry.changedAt, locale)}</td>
            {/* Первый переход — это заведение сделки: стадии «откуда» у него нет, и
                прочерк честнее, чем подставленное название первой стадии. */}
            <td>{entry.fromStageName ?? t('card.deal.history.created')}</td>
            <td>
              {entry.toStageName}
              {/* Возврат помечается СЛОВОМ, а не только цветом строки: цветовой признак
                  не переживает ни печать, ни дальтонизм — а возврат назад это и есть то,
                  ради чего вкладку открывают. */}
              {entry.backwards && (
                <span className="badge"> {t('card.deal.history.backwards')}</span>
              )}
            </td>
            <td>{formatDuration(entry.durationSeconds, t)}</td>
            <td>{entry.changedByName ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function StageHistoryPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['deal-stage-history', recordId],
    queryFn: ({ signal }) => fetchStageHistory(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.deal.history.empty')}>
      {(entries: StageHistoryEntry[]) => (
        <div className="card-gap">
          <HistoryTable entries={entries} />
          {/* Счётчик возвратов — сводка того же журнала. Стадия, на которую постоянно
              возвращаются, это находка отчёта (#45), и увидеть её в списке из тридцати
              строк без счётчика трудно. */}
          <p className="muted">
            {t('card.deal.history.summary', {
              total: entries.length,
              backwards: entries.filter((entry) => entry.backwards).length,
            })}
          </p>
        </div>
      )}
    </AsyncSection>
  )
}

/**
 * Вкладка «История стадий».
 *
 * Читает журнал переходов, а не текущее состояние: сделка, вернувшаяся на шаг назад и
 * снова ушедшая вперёд, по текущей стадии видна один раз, а прошла её дважды.
 */
export function stageHistoryTab(): CardTab {
  return {
    id: 'stages',
    labelKey: 'card.tab.stageHistory',
    render: (context: CardTabContext) => <StageHistoryPanel recordId={context.recordId} />,
  }
}

/** Момент времени: дата и время вместе — «когда именно» спрашивают о переходах. */
function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}
